"use client"

import { useState, useMemo, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Save, Loader2 } from "lucide-react"
import { ROLE_PERMISSIONS, VIEWER_GRANTABLE_PERMISSIONS, SUPER_USER_ONLY_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { getEffectivePermissions } from "@/lib/auth/permissions.utils"
import { useSession } from "next-auth/react"
import type { User } from "@/hooks/queries/users"

interface UserPermissionsDialogProps {
  /** Target user whose permissions are being edited; `null` keeps the dialog dormant. */
  user: User | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdatePermissions: (uuid: string, additionalPermissions: string[], disabledPermissions: string[]) => Promise<void>
}

// Group permissions by resource
const groupPermissions = (permissions: string[]) => {
  const grouped: Record<string, string[]> = {}
  permissions.forEach(permission => {
    const [resource] = permission.split('.')
    if (!grouped[resource]) {
      grouped[resource] = []
    }
    grouped[resource].push(permission)
  })
  return grouped
}

export function UserPermissionsDialog({ user, open, onOpenChange, onUpdatePermissions }: UserPermissionsDialogProps) {
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    user?.additional_permissions || []
  )
  const [disabledPermissions, setDisabledPermissions] = useState<string[]>(
    user?.disabled_permissions || []
  )
  const [isSaving, setIsSaving] = useState(false)
  // The page-level status banner sits behind the dialog overlay, so a failed save
  // is surfaced inline here instead.
  const [saveError, setSaveError] = useState<string | null>(null)

  // Re-sync the editable selection whenever the dialog targets a different user
  // (or that user's persisted permissions change under us). Depending on the whole
  // `user` object — not its permission arrays — is deliberate: switching between two
  // users who BOTH have no persisted permissions still changes the object reference,
  // so the local selection is reset instead of leaking from the previous target.
  useEffect(() => {
    setSelectedPermissions(user?.additional_permissions || [])
    setDisabledPermissions(user?.disabled_permissions || [])
  }, [user])

  const currentUser = useSession().data?.user ?? null

  // Get permissions that current user can grant - based on their effective permissions
  // User can only grant permissions they themselves have
  const availablePermissions = useMemo(() => {
    if (!currentUser?.role) return []

    // Single source of truth: the shared (server-safe) effective-permissions
    // resolver. `currentUser` is the decoded JWT (UserAuthData) and already
    // carries role + additional + disabled, so pass it whole. This keeps
    // integrator (and any future role) behaving identically to the server.
    const effective = getEffectivePermissions(currentUser) as string[]

    // Hide what the current grantor cannot actually grant. SUPER_USER_ONLY perms
    // are grantable only by a super_user (mirrors validatePermissionGrant); a
    // non-super grantor who happens to hold one (e.g. a cross-tenant grant in
    // their own additional_permissions) would otherwise see it here and hit
    // "Cannot grant permissions you don't have" / "...yourself" on save.
    if (currentUser.role === "super_user") return effective
    return effective.filter((p) => !(SUPER_USER_ONLY_PERMISSIONS as readonly string[]).includes(p))
  }, [currentUser])

  // Get permissions from user's role
  // Note: VIEWER_GRANTABLE_PERMISSIONS are never "from role" - they must be explicitly granted
  const rolePermissions = useMemo(() => {
    const perms = ROLE_PERMISSIONS[user?.role as keyof typeof ROLE_PERMISSIONS] || []
    // If role has wildcard, return available permissions (filtered by current user)
    // Check if perms array contains wildcard (for super_user role)
    if (perms.length > 0 && (perms as string[])[0] === '*') {
      // Exclude viewer-grantable permissions - they are never "from role" for display purposes
      return availablePermissions.filter(p => !(VIEWER_GRANTABLE_PERMISSIONS as readonly string[]).includes(p))
    }
    // Expand wildcards like "integrations.*"
    const expandedSet = new Set<string>()
    perms.forEach(perm => {
      const permStr = perm as string
      if (permStr.endsWith('.*')) {
        const resource = permStr.split('.')[0]
        const matching = availablePermissions.filter(p => p.startsWith(`${resource}.`))
        matching.forEach(m => expandedSet.add(m))
      } else {
        if (availablePermissions.includes(permStr)) {
          expandedSet.add(permStr)
        }
      }
    })
    return [...expandedSet]
  }, [user?.role, availablePermissions])

  const groupedPermissions = useMemo(() => groupPermissions(availablePermissions), [availablePermissions])

  const handleTogglePermission = (permission: string, isFromRole: boolean) => {
    if (isFromRole) {
      // Role permissions: toggle in disabled_permissions
      setDisabledPermissions(prev =>
        prev.includes(permission)
          ? prev.filter(p => p !== permission)
          : [...prev, permission]
      )
    } else {
      // Additional permissions: toggle in selected_permissions
      setSelectedPermissions(prev =>
        prev.includes(permission)
          ? prev.filter(p => p !== permission)
          : [...prev, permission]
      )
    }
  }

  const handleSave = async () => {
    if (!user) return
    setIsSaving(true)
    setSaveError(null)
    try {
      await onUpdatePermissions(user.uuid, selectedPermissions, disabledPermissions)
      onOpenChange(false)
    } catch (error) {
      // Keep the dialog open with the edits intact so the user can retry.
      setSaveError(error instanceof Error ? error.message : "Failed to update permissions")
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    // Discard unsaved toggles back to the persisted values before closing.
    setSelectedPermissions(user?.additional_permissions || [])
    setDisabledPermissions(user?.disabled_permissions || [])
    setSaveError(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : handleCancel())}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col gap-0">
        <DialogHeader>
          <DialogTitle>Manage Permissions</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1 text-xs">
              <p>
                <span className="font-medium">Role:</span> {user?.role}
              </p>
              <p>
                <span className="inline-block w-3 h-3 bg-muted border rounded mr-1 align-middle"></span>
                <span>= from role (click to disable)</span>
              </p>
              <p>
                <span className="line-through">Strikethrough</span> <span>= disabled permission</span>
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable permission list */}
        <div className="overflow-y-auto flex-1 min-h-0 -mx-6 px-6 py-3 border-y">
          <div className="space-y-4">
            {Object.entries(groupedPermissions).map(([resource, permissions]) => (
              <div key={resource} className="space-y-2">
                <h4 className="font-medium text-sm capitalize border-b pb-1">
                  {resource.replace('_', ' ')}
                </h4>
                <div className="space-y-2 pl-2">
                  {permissions.map(permission => {
                    const isFromRole = rolePermissions.includes(permission)
                    const isDisabled = disabledPermissions.includes(permission)
                    const isAdditional = selectedPermissions.includes(permission)
                    // If from role and not disabled → checked
                    // If from role and disabled → unchecked
                    // If not from role and additional → checked
                    // If not from role and not additional → unchecked
                    const isChecked = isFromRole ? !isDisabled : isAdditional

                    return (
                      <div key={permission} className="flex items-center space-x-2">
                        <Checkbox
                          id={`perm-${permission}`}
                          checked={isChecked}
                          disabled={isSaving}
                          onCheckedChange={() => handleTogglePermission(permission, isFromRole)}
                          className={isFromRole && !isDisabled ? "opacity-70" : ""}
                        />
                        <Label
                          htmlFor={`perm-${permission}`}
                          className={`text-sm cursor-pointer ${
                            isDisabled ? "line-through text-muted-foreground" : ""
                          } ${isFromRole && !isDisabled ? "text-muted-foreground" : ""}`}
                        >
                          {permission}
                          {isFromRole && !isDisabled && (
                            <span className="ml-2 text-xs text-muted-foreground">(from role)</span>
                          )}
                          {isDisabled && (
                            <span className="ml-2 text-xs text-destructive">(disabled)</span>
                          )}
                        </Label>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="pt-4 sm:items-center">
          {saveError && (
            <p className="text-sm text-destructive sm:mr-auto" role="alert">
              {saveError}
            </p>
          )}
          <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                <span key="saving">Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-1" />
                <span key="save">Save</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
